import { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { config } from "@/config";
import { storeTokens, clearStoredTokens, type CognitoTokens } from "@/auth/cognitoTokens";
const FAILED_ATTEMPTS_KEY = "auth_failed_attempts";
const MAX_FAILED_ATTEMPTS = 2;
const AUTH_EXCHANGE_ENDPOINT = "/api/auth/exchange-code";
const LOGIN_PATH = "/login";

/**
 * Sanitize a Cognito authorization code by stripping all characters
 * outside the expected set (lowercase hex + hyphens).
 * Returns null if the result is empty or too short to be valid.
 */
function sanitizeAuthCode(raw: string): string | null {
  const cleaned = raw.replace(/[^a-f0-9-]/g, "");
  return cleaned.length >= 30 ? cleaned : null;
}

function getFailedAttempts(): number {
  const stored = localStorage.getItem(FAILED_ATTEMPTS_KEY);
  return stored ? parseInt(stored, 10) : 0;
}

function incrementFailedAttempts(): number {
  const current = getFailedAttempts();
  const newCount = current + 1;
  localStorage.setItem(FAILED_ATTEMPTS_KEY, newCount.toString());
  return newCount;
}

function clearFailedAttempts() {
  localStorage.removeItem(FAILED_ATTEMPTS_KEY);
}

export default function LoginPage() {
  const { loading, isAuthenticated, loginWithCognitoTokens } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [exchanging, setExchanging] = useState(false);
  const [exchangeError, setExchangeError] = useState<string | null>(null);
  const exchangeStartedRef = useRef(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");

    const failedAttempts = getFailedAttempts();
    if (failedAttempts >= MAX_FAILED_ATTEMPTS && !code) {
      setExchangeError(
        "Authentication failed multiple times. This may be due to a configuration issue. Please contact your administrator."
      );
      return;
    }

    const sanitizedCode = code ? sanitizeAuthCode(code) : null;

    if (sanitizedCode && !exchangeStartedRef.current) {
      exchangeStartedRef.current = true;
      setExchanging(true);

      window.history.replaceState({}, "", window.location.pathname);

      fetch(AUTH_EXCHANGE_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code: sanitizedCode, redirect_uri: LOGIN_PATH }),
      })
        .then((res) => {
          if (!res.ok) {
            throw new Error(`Token exchange failed: ${res.status}`);
          }
          return res.json();
        })
        .then(async (data) => {
          const tokens: CognitoTokens = {
            idToken: data.id_token,
            accessToken: data.access_token,
            refreshToken: data.refresh_token,
            expiresIn: data.expires_in,
          };

          clearFailedAttempts();
          storeTokens(tokens);
          await loginWithCognitoTokens(tokens);

          const from = (location.state as any)?.from?.pathname || "/";
          navigate(from, { replace: true });
        })
        .catch((error) => {
          console.error("Authorization code exchange failed:", error);
          clearStoredTokens();

          const newCount = incrementFailedAttempts();
          if (newCount >= MAX_FAILED_ATTEMPTS) {
            setExchangeError(
              "Authentication failed multiple times. This may be due to a configuration issue. Please contact your administrator."
            );
          }

          exchangeStartedRef.current = false;
          setExchanging(false);
        });

      return;
    }

    if (!loading && isAuthenticated) {
      const from = (location.state as any)?.from?.pathname || "/";
      navigate(from, { replace: true });
      return;
    }

    if (
      !loading &&
      !isAuthenticated &&
      !code &&
      !exchanging &&
      !exchangeError &&
      config.cognitoDomain &&
      config.cognitoClientId
    ) {
      const redirectUri = encodeURIComponent(window.location.origin + "/login");
      const cognitoLoginUrl = `https://${config.cognitoDomain}/oauth2/authorize?client_id=${config.cognitoClientId}&response_type=code&redirect_uri=${redirectUri}&scope=${config.cognitoScopes}`;

      console.log("Redirecting to Cognito Hosted UI (authorization code flow):", cognitoLoginUrl);
      window.location.href = cognitoLoginUrl;
    }
  }, [loading, isAuthenticated, exchanging, exchangeError, loginWithCognitoTokens, navigate, location]);

  if (exchangeError) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-xl font-bold text-red-600 mb-4">Authentication Error</h2>
          <p className="text-gray-700 mb-4">{exchangeError}</p>
          <button
            onClick={() => {
              clearFailedAttempts();
              setExchangeError(null);
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (exchanging) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        Exchanging authorization code...
      </div>
    );
  }

  return <div className="flex items-center justify-center min-h-screen">Redirecting...</div>;
}
